import React, { useCallback, useEffect, useState } from 'react';
import { CircleHelp } from 'lucide-react';
import { getPontos, salvarSaldoDePontos } from '../services/api';
import { ProgramaDePontos } from '../types/PaymentMethod';
import { mensagemDeErro } from '../utils/mensagemDeErro';
import './SeusPontos.css';

/**
 * Os pontos dela, um programa por linha.
 *
 * O saldo não vem de API nenhuma (não existe): é o que ela conferiu no
 * programa, e dali em diante o app soma os pontos das compras pela pontuação
 * de cada cartão. Por isso o número aparece como "cerca de", com a data da
 * última conferência, e "Atualizar" é a ação principal: quando o saldo real
 * muda (resgate, bônus), ela corrige aqui e a conta recomeça.
 *
 * Some inteiro quando o servidor ainda não guarda pontos. Sem programa
 * conhecido nos cartões dela, fica uma linha e o "?": é justamente quem não
 * sabe o que são pontos que precisa descobrir que eles existem.
 */

const qtd = (n: number) => Math.round(n).toLocaleString('pt-BR');
const reais = (n: number) => `R$ ${Math.round(n).toLocaleString('pt-BR')}`;
const dataBR = (iso: string) => { const [a, m, d] = iso.split('-'); return `${d}/${m}/${a}`; };

interface Props {
  /** Muda quando os métodos mudam (uma pontuação nova), para a estimativa ser relida. */
  versao?: number;
}

export const SeusPontos: React.FC<Props> = ({ versao = 0 }) => {
  const [programas, setProgramas] = useState<ProgramaDePontos[]>([]);
  /** O servidor guarda pontos? Sem isso o bloco não tem o que oferecer, e some. */
  const [disponivel, setDisponivel] = useState(false);
  /** Os programas da tabela que ela ainda não tem: para acrescentar à mão. */
  const [outros, setOutros] = useState<{ id: string; nome: string }[]>([]);
  /** O formulário de acrescentar um programa está aberto, e qual foi escolhido. */
  const [novo, setNovo] = useState<string | null>(null);
  /** A explicação do "?" está aberta. */
  const [explicando, setExplicando] = useState(false);
  const [editando, setEditando] = useState<string | null>(null);
  const [saldo, setSaldo] = useState('');
  const [vence, setVence] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const ler = useCallback(async () => {
    try {
      const r = await getPontos();
      setDisponivel(r.disponivel);
      setProgramas(r.disponivel ? r.programas : []);
      setOutros(r.disponivel ? r.outros ?? [] : []);
    } catch {
      // Sem os pontos a tela de métodos continua inteira: este bloco só não aparece.
      setDisponivel(false);
      setProgramas([]);
    }
  }, []);

  useEffect(() => { ler(); }, [ler, versao]);

  if (!disponivel) return null;

  const abrir = (p: ProgramaDePontos) => {
    setNovo(null);
    setEditando(p.id);
    setSaldo(p.estimado !== null ? String(Math.round(p.estimado)) : '');
    setVence(p.venceEm ?? '');
    setErro(null);
  };

  const salvar = async (p: { id: string }) => {
    const n = Number(saldo.replace(/\./g, '').replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) { setErro('Informe o saldo em pontos.'); return; }
    setSalvando(true);
    try {
      await salvarSaldoDePontos(p.id, Math.round(n), vence || null);
      setEditando(null);
      setNovo(null);
      await ler();
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui salvar o saldo.'));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <section className="pts" aria-label="Seus pontos">
      <div className="pts-topo">
        <h3 className="pts-titulo">Seus pontos</h3>
        {/*
          O "?" (pedido dela, 09/10): quem nunca usou pontos não sabe o que é
          este bloco, nem que o cartão pode estar juntando pontos sem ela
          saber. Abre aqui mesmo, sem janela por cima.
        */}
        <button
          type="button" className="pts-ajuda" aria-expanded={explicando}
          aria-label="O que são pontos e como usar" onClick={() => setExplicando(v => !v)}
        >
          <CircleHelp size={16} aria-hidden="true" />
        </button>
      </div>
      {explicando && (
        <div className="pts-explica">
          <p>
            Muitos cartões de crédito dão <strong>pontos a cada compra</strong>. Eles ficam num programa
            (Esfera, Livelo ou o do seu banco) e podem virar passagem aérea, desconto na fatura ou produto.
          </p>
          <p>
            É dinheiro que você já gastou voltando para você. Em passagem os pontos costumam valer mais;
            em produto do catálogo, menos. E eles vencem: quem não acompanha, perde.
          </p>
          <p>
            Informe aqui o saldo que aparece no programa. Daí em diante eu somo os pontos das suas compras
            e aviso antes de vencerem. Não sabe se o seu cartão pontua? Veja no app do banco, em
            pontos ou benefícios.
          </p>
        </div>
      )}
      {programas.length === 0 && (
        <p className="pts-detalhe">Nenhum programa por aqui ainda. Se você junta pontos, com ou sem cartão, adicione o programa abaixo.</p>
      )}
      {programas.map(p => (
        <div key={p.id} className="pts-linha">
          {/*
            Uma linha por programa: nome, saldo e ação. Ela achou o bloco grande
            demais (09/10): o detalhe da conferência desceu para uma linha miúda,
            e o convite a informar virou só o botão.
          */}
          <div className="pts-cabeca">
            <a className="pts-nome" href={p.link} target="_blank" rel="noreferrer">{p.nome}</a>
            {p.estimado !== null && editando !== p.id && (
              // Milhas na companhia são o número que ela informou, sem "cerca de".
              <span className="pts-saldo">
                {p.unidade === 'milhas' ? '' : 'cerca de '}<strong>{qtd(p.estimado)}</strong>{p.unidade === 'milhas' ? ' milhas' : ''}
              </span>
            )}
            {editando !== p.id && (
              <button type="button" className="pts-acao" onClick={() => abrir(p)}>
                {p.saldo === null ? 'Informar saldo' : 'Atualizar'}
              </button>
            )}
          </div>

          {editando === p.id ? (
            <div className="pts-form">
              <label>
                Saldo que aparece no programa
                <input
                  inputMode="numeric" value={saldo} autoFocus
                  onChange={e => setSaldo(e.target.value)} placeholder="52000"
                />
              </label>
              <label>
                Vencem em <span className="pts-opcional">(opcional)</span>
                <input type="date" value={vence} onChange={e => setVence(e.target.value)} />
              </label>
              {erro && <p className="pts-erro">{erro}</p>}
              <div className="pts-botoes">
                <button type="button" className="pts-salvar" disabled={salvando} aria-busy={salvando} onClick={() => salvar(p)}>
                  Salvar
                </button>
                <button type="button" className="pts-cancelar" onClick={() => setEditando(null)}>Cancelar</button>
              </div>
            </div>
          ) : p.estimado !== null && (
            <p className="pts-detalhe">
              {/* O valor em reais vem primeiro: é o número que faz a pessoa dar atenção aos pontos. */}
              {p.valeDe != null && p.valeAte != null && p.valeAte > 0 && `valem de ${reais(p.valeDe)} a ${reais(p.valeAte)} · `}
              {qtd(p.saldo ?? 0)} em {dataBR(p.informadoEm!)}
              {(p.ganhoDesde ?? 0) > 0 && ` + ${qtd(p.ganhoDesde!)} das compras`}
              {p.venceEm && ` · vencem em ${dataBR(p.venceEm)}`}
              {p.faltaTaxa && p.cartoes.length > 0 && ' · informe quanto o cartão pontua, editando-o'}
              {!p.faltaTaxa && p.semDolar && ' · sem a cotação do dólar agora: as compras entram na conta assim que ela chegar'}
              {p.cartoes.length === 0 && p.unidade !== 'milhas' && ' · sem cartão: o saldo só muda quando você atualizar'}
            </p>
          )}
        </div>
      ))}
      {/*
        Dá para estar num programa sem ter cartão de banco nenhum (ela, 09/10):
        quem junta pontos por compra em loja parceira, por clube ou por
        transferência. Aqui ela acrescenta o programa e o saldo à mão.
      */}
      {outros.length > 0 && (novo === null ? (
        <button
          type="button" className="pts-acao pts-novo"
          onClick={() => { setNovo(outros[0].id); setEditando(null); setSaldo(''); setVence(''); setErro(null); }}
        >
          + Adicionar programa
        </button>
      ) : (
        <div className="pts-form">
          <label>
            Programa
            <select value={novo} onChange={e => setNovo(e.target.value)}>
              {outros.map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
            </select>
          </label>
          <label>
            Saldo que aparece no programa
            <input inputMode="numeric" value={saldo} onChange={e => setSaldo(e.target.value)} placeholder="52000" />
          </label>
          <label>
            Vencem em <span className="pts-opcional">(opcional)</span>
            <input type="date" value={vence} onChange={e => setVence(e.target.value)} />
          </label>
          {erro && <p className="pts-erro">{erro}</p>}
          <div className="pts-botoes">
            <button type="button" className="pts-salvar" disabled={salvando} aria-busy={salvando} onClick={() => salvar({ id: novo })}>
              Salvar
            </button>
            <button type="button" className="pts-cancelar" onClick={() => setNovo(null)}>Cancelar</button>
          </div>
        </div>
      ))}
    </section>
  );
};
